import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, Avatar, Chip, TextField,
  Button, CircularProgress, Tooltip, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import useAuth from '../../hooks/useAuth';

const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CommentThreadPanel = ({
  open,
  onClose,
  sectionTitle,
  comments,
  isLoading,
  onAddComment,
  onReply,
  onResolve,
  onReopen,
  onDelete,
}) => {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const inputRef = useRef(null);
  const replyRef = useRef(null);

  const userId = user?.id || user?._id;

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  useEffect(() => {
    if (replyingTo && replyRef.current) {
      replyRef.current.focus();
    }
  }, [replyingTo]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    await onAddComment(newComment.trim());
    setNewComment('');
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !replyingTo) return;
    await onReply(replyingTo, replyText.trim());
    setReplyText('');
    setReplyingTo(null);
  };

  const openCount = comments.filter(c => c.status === 'open').length;
  const resolvedCount = comments.filter(c => c.status === 'resolved').length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 440, bgcolor: '#fafafa',
          display: 'flex', flexDirection: 'column',
          top: '70px', height: 'calc(100% - 70px)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 2.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#fff', borderBottom: '1px solid #e5e5e5', flexShrink: 0
      }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#475969', fontFamily: "'Sofia Pro Bold', sans-serif" }}>
            Comments
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#72849a', mt: 0.25 }}>
            {sectionTitle || 'Section'}
          </Typography>
          {(openCount > 0 || resolvedCount > 0) && (
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              {openCount > 0 && <Chip label={`${openCount} open`} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#fff3e0', color: '#e65100' }} />}
              {resolvedCount > 0 && <Chip label={`${resolvedCount} resolved`} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#e8f5e9', color: '#2e7d32' }} />}
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#475969' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Comment input — always visible at top */}
      <Box sx={{ p: 2, px: 3, bgcolor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <TextField
          inputRef={inputRef}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          size="small"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmitComment();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: 13,
              '& fieldset': { borderColor: '#e0e0e0' },
              '&:hover fieldset': { borderColor: '#bdbdbd' },
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={!newComment.trim()}
            onClick={handleSubmitComment}
            endIcon={<SendIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none', fontSize: 12, fontWeight: 500,
              bgcolor: '#475969', '&:hover': { bgcolor: '#34454f' },
              px: 2, py: 0.5, minHeight: 30, borderRadius: 1.5,
            }}
          >
            Comment
          </Button>
        </Box>
      </Box>

      {/* Comments list */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, px: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} sx={{ color: '#475969' }} />
          </Box>
        ) : comments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e5e5' }}>
            <Typography sx={{ fontSize: 14, color: '#72849a' }}>No comments yet</Typography>
            <Typography sx={{ fontSize: 12, color: '#a0aec0', mt: 0.5 }}>
              Add the first comment above
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {comments.map(comment => {
              const isResolved = comment.status === 'resolved';
              const isAuthor = comment.created_by?.id === userId;
              const initial = (comment.created_by?.name || '?').charAt(0).toUpperCase();

              return (
                <Box key={comment.id} sx={{ opacity: isResolved ? 0.6 : 1 }}>
                  {/* Main comment */}
                  <Box sx={{
                    bgcolor: '#fff', borderRadius: 2,
                    border: `1px solid ${isResolved ? '#c8e6c9' : '#e5e5e5'}`,
                    borderLeft: `3px solid ${isResolved ? '#2e7d32' : '#1976d2'}`,
                    p: 2,
                  }}>
                    {/* Header: avatar + name + time + actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 600, bgcolor: '#1976d2' }}>
                        {initial}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475969' }}>
                          {comment.created_by?.name || 'Unknown'}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#a0aec0' }}>
                          {getTimeAgo(comment.created_at)}
                        </Typography>
                      </Box>
                      {/* Actions */}
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        {!isResolved ? (
                          <Tooltip title="Resolve">
                            <IconButton size="small" onClick={() => onResolve(comment.id)} sx={{ color: '#72849a', p: 0.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reopen">
                            <IconButton size="small" onClick={() => onReopen(comment.id)} sx={{ color: '#72849a', p: 0.5 }}>
                              <ReplayIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAuthor && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => onDelete(comment.id)} sx={{ color: '#ef9a9a', p: 0.5 }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Selected text quote */}
                    {comment.selected_text && (
                      <Box sx={{
                        mt: 1.5, p: 1, bgcolor: '#f8f9fa', borderRadius: 1,
                        borderLeft: '2px solid #e2e8f0', display: 'flex', gap: 0.5
                      }}>
                        <FormatQuoteIcon sx={{ fontSize: 14, color: '#a0aec0', mt: 0.25 }} />
                        <Typography sx={{ fontSize: 12, color: '#4a5568', fontStyle: 'italic', lineHeight: 1.4 }}>
                          {comment.selected_text.length > 150
                            ? comment.selected_text.substring(0, 150) + '...'
                            : comment.selected_text}
                        </Typography>
                      </Box>
                    )}

                    {/* Comment body */}
                    <Typography sx={{ mt: 1.5, fontSize: 13, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {comment.content}
                    </Typography>

                    {/* Reply button */}
                    {!isResolved && (
                      <Button
                        size="small"
                        onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                        sx={{ mt: 1, textTransform: 'none', fontSize: 11, color: '#72849a', p: 0, minWidth: 'auto' }}
                      >
                        Reply{comment.reply_count > 0 ? ` (${comment.reply_count})` : ''}
                      </Button>
                    )}
                  </Box>

                  {/* Replies */}
                  {comment.replies?.length > 0 && (
                    <Box sx={{ ml: 3, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {comment.replies.map(reply => {
                        const rInitial = (reply.created_by?.name || '?').charAt(0).toUpperCase();
                        const rIsAuthor = reply.created_by?.id === userId;
                        return (
                          <Box key={reply.id} sx={{
                            bgcolor: '#fff', borderRadius: 1.5,
                            border: '1px solid #f0f0f0', p: 1.5,
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 600, bgcolor: '#7c3aed' }}>
                                {rInitial}
                              </Avatar>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475969', flex: 1 }}>
                                {reply.created_by?.name}
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: '#a0aec0' }}>
                                {getTimeAgo(reply.created_at)}
                              </Typography>
                              {rIsAuthor && (
                                <IconButton size="small" onClick={() => onDelete(reply.id)} sx={{ color: '#ef9a9a', p: 0.25 }}>
                                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </Box>
                            <Typography sx={{ mt: 0.75, fontSize: 12, color: '#374151', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                              {reply.content}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Reply input */}
                  {replyingTo === comment.id && (
                    <Box sx={{ ml: 3, mt: 1 }}>
                      <TextField
                        inputRef={replyRef}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write a reply..."
                        multiline
                        minRows={1}
                        maxRows={3}
                        fullWidth
                        size="small"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            handleSubmitReply();
                          }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: 12 } }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                        <Button size="small" onClick={() => { setReplyingTo(null); setReplyText(''); }}
                          sx={{ textTransform: 'none', fontSize: 11, color: '#72849a' }}>Cancel</Button>
                        <Button size="small" variant="contained" disabled={!replyText.trim()} onClick={handleSubmitReply}
                          sx={{ textTransform: 'none', fontSize: 11, bgcolor: '#475969', '&:hover': { bgcolor: '#34454f' }, px: 1.5, minHeight: 26, borderRadius: 1 }}>
                          Reply
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default React.memo(CommentThreadPanel);
